import {useState, useEffect, useCallback, useRef} from 'react'

/**
 * Custom hook to track the size (width and height) of an element using a ref
 * @param {object} options - Optional configuration
 * @param {number} options.debounceMs - Debounce delay for resize events (default: 100)
 * @returns {object} - { ref, width, height, isReady }
 *
 * @example
 * const { ref, width, height, isReady } = useElementSize()
 * return <div ref={ref}>Width: {width}, Height: {height}</div>
 */
const useElementSize = (options = {}) => {
	const {debounceMs = 100} = options

	const ref = useRef(null)
	const [size, setSize] = useState({width: 0, height: 0})
	const [isReady, setIsReady] = useState(false)

	const updateSize = useCallback(() => {
		if (!ref.current) return

		const {offsetWidth, offsetHeight} = ref.current
		setSize({width: offsetWidth, height: offsetHeight})
		setIsReady(true)
	}, [])

	useEffect(() => {
		const element = ref.current
		if (!element) return

		// Initial size measurement
		updateSize()

		let resizeObserver
		let debounceTimer

		// Check if ResizeObserver is supported
		if (typeof ResizeObserver !== 'undefined') {
			resizeObserver = new ResizeObserver(() => {
				// Debounce the size update
				clearTimeout(debounceTimer)
				debounceTimer = setTimeout(() => {
					updateSize()
				}, debounceMs)
			})

			resizeObserver.observe(element)
		} else {
			// Fallback to window resize event if ResizeObserver is not supported
			const handleResize = () => {
				clearTimeout(debounceTimer)
				debounceTimer = setTimeout(() => {
					updateSize()
				}, debounceMs)
			}

			window.addEventListener('resize', handleResize)

			return () => {
				window.removeEventListener('resize', handleResize)
				clearTimeout(debounceTimer)
			}
		}

		return () => {
			if (resizeObserver) {
				resizeObserver.disconnect()
			}
			clearTimeout(debounceTimer)
		}
	}, [debounceMs, updateSize])

	return {
		ref,
		width: size.width,
		height: size.height,
		isReady,
		refresh: updateSize,
	}
}

export default useElementSize
